import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { IFileUploadResult } from '../../models/i-file-upload-result';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { FileService } from '../../services/file.service';
import { ClientDocumentService } from '../../../core/services/client-document.service';
import { Subject, takeUntil } from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';


const kilobyte = 1024;
const megabyte = 1024 * kilobyte;

@Component({
  selector: 'app-file-upload-dialog',
  templateUrl: './file-upload-dialog.component.html',
  styleUrl: './file-upload-dialog.component.scss'
})
export class FileUploadDialogComponent {

  private destroy$ = new Subject<void>();
  private readonly MAX_UPLOAD_SIZE = (28 * megabyte) + (8 * kilobyte);
  private readonly maxUploadSizeText = '28MB';
  private readonly MAX_INDIVIDUAL_UPLOAD_SIZE = (5 * megabyte) + (8 * kilobyte);
  private readonly maxIndividualUploadSizeText = '5MB';

    maxTotalUploadSize = this.MAX_UPLOAD_SIZE;
  maxTotalUploadSizeText = this.maxUploadSizeText;
  private uploadSubscription?: any;
  public abortController?: AbortController;

  isBusy = false;
  errorMessage: string | null = null;
  selectedFile: File | null = null;
  uploadDisabled = true;
  removeDisabled = true;
  isDragOver = false;
  uploadProgress = 0;
  uploadType: string | null = null;
  matterId: string | null = null;
  clientId: number | null = null;

  uploadSpeed = 0; // bytes per second
  estimatedTimeRemaining = 0; // seconds
  private uploadStartTime = 0;
  private lastProgressTime = 0;
  private lastLoadedBytes = 0;
  private breakPointState: BreakpointState | null = null;

  constructor(private fileService: FileService, private breakpointObserver: BreakpointObserver,public dialogRef: MatDialogRef<FileUploadDialogComponent>, private clientDocumentService: ClientDocumentService,@Inject(MAT_DIALOG_DATA) public data: any) {
    this.uploadType = data.uploadType;
    this.matterId = data.matterId;
    this.clientId = data.clientId;
  }

  // I don't like this.. Maybe need to refactor these into a seperate service that deals with file size limits and allowed file types?
  getAllowedFileTypesString(): string {
    return this.clientDocumentService.allowedFileTypes.join(', ');
  }

  getAllowedFileTypesComparisonString(): string {
    return this.clientDocumentService.allowedFileTypes
      .map(type => type.replace(/^\./, '')).join('|');
  }

    ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
    if (this.abortController) {
      this.abortController.abort();
    }
  }

    isDragging: boolean = false;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]); // Only take the first file
    }
  }

  onFileSelected(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const files = inputElement.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]); // Only take the first file
    }
    // Clear the input value so the same file can be selected again if it has been removed
    inputElement.value = '';
  }

    private handleFile(file: File) {
    this.errorMessage = null;
    this.selectedFile = file;
    this.validateFile();
  }

  // again, i'd like to see this is in a shared service as it's a copy nad paste from the client document upload
    private validateFile() {
    this.errorMessage = null;
    this.uploadDisabled = true;
    this.removeDisabled = false;

    if (!this.selectedFile) {
      this.uploadDisabled = true;
      this.removeDisabled = true;
      return;
    }

    const regex = new RegExp(`\.(${this.getAllowedFileTypesComparisonString()})$`, 'i');

    // Check for invalid characters in file name
    const invalidCharRegex = /[^0-9a-zA-Z\-_,&(). ]+/;
    if (invalidCharRegex.test(this.selectedFile.name)) {
      this.errorMessage = `The file name contains invalid characters. Allowed characters are: letters, numbers, dash (-), underscore (_), comma (,), ampersand (&), parentheses ( ), and space. Please rename the file and try again.`;
      this.uploadDisabled = true;
      return;
    }

    // Check for file type
    if (!regex.test(this.selectedFile.name)) {
      this.errorMessage = `The file has an invalid file type. Please upload a file with one of the following extensions: ${this.getAllowedFileTypesString()}`;
      this.uploadDisabled = true;
      return;
    }
    
    if (this.selectedFile.size === 0) {
      this.errorMessage = 'The selected file is empty. Please choose a file that contains data.';
      this.uploadDisabled = true;
      return;
    } 

    // Check if file is larger than the max individual size
    if (this.selectedFile.size > this.MAX_INDIVIDUAL_UPLOAD_SIZE) {
      const sizeInMb = this.selectedFile.size / megabyte;
      this.errorMessage = `The maximum file size limit is ${this.maxIndividualUploadSizeText}. The file you are attempting to upload is ${sizeInMb.toFixed(2)}MB. Please compress the file to ensure it is below the ${this.maxIndividualUploadSizeText} limit.`;
      this.uploadDisabled = true;
      return;
    }

    this.uploadDisabled = false;
    this.errorMessage = null;
  }

    removeFile(): void {
    if (this.isBusy) {
      throw new Error("Cannot remove file while uploading");
    }

    this.errorMessage = null;
    this.selectedFile = null;
    this.removeDisabled = true;
    this.uploadDisabled = true;
    this.uploadProgress = 0;
  }

    uploadFiles(): void {
 if (this.isBusy || this.uploadDisabled || !this.selectedFile) {
    return;
  }

  this.uploadStartTime = Date.now();
  this.lastProgressTime = this.uploadStartTime;
  this.lastLoadedBytes = 0;
  this.uploadSpeed = 0;
  this.estimatedTimeRemaining = 0;

  this.isBusy = true;
  this.uploadDisabled = true;
  this.removeDisabled = true;
  this.errorMessage = null;
  this.uploadProgress = 0;

  this.abortController = new AbortController();

  const uploadRequest = {
    matterId: this.matterId ?? '',
    type: this.uploadType ?? '',
    description: '',
    fileId: '',
    file: this.selectedFile
  };

  const timeoutId = setTimeout(() => {
    if (this.isBusy && this.uploadSubscription) {
      this.cancelUpload();
    }
  }, 180000);

  this.uploadSubscription = this.fileService.uploadFile(uploadRequest)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.updateProgressMetrics(event.loaded, event.total);
        } else if (event.type === HttpEventType.Response) {
          clearTimeout(timeoutId);
          this.handleUploadSuccess(event.body!);
        }
      },
      error: (error) => {
        clearTimeout(timeoutId);
        this.handleUploadError(error);
      },
      complete: () => {
        clearTimeout(timeoutId);
      }
    });
  }

  private handleUploadSuccess(result: IFileUploadResult) {
  this.isBusy = false;
  this.uploadProgress = 100;
  this.abortController = undefined;
  
  // Clean up selected file
  this.removeFile();
  // Emit success event
  this.dialogRef.close(result);
  
}

private handleUploadError(error: any) {
  console.log('Upload error occurred:', error);
  
  this.isBusy = false;
  this.removeDisabled = false;
  this.uploadDisabled = false;
  this.uploadProgress = 0;
  this.abortController = undefined;

  if (this.uploadSubscription) {
    this.uploadSubscription.unsubscribe();
    this.uploadSubscription = null;
  }

  // Handle different types of errors
  if (error.name === 'AbortError' || error.status === 0 || error.status === 408) {
    // Request was cancelled
    this.errorMessage = "Upload cancelled.";
    this.dialogRef.close(this.errorMessage);
  } else {
    // Other errors
    this.errorMessage = error.error?.message || 'An error occurred during upload.';
    //this.dialogRef.close(this.errorMessage);
  }
}

  private updateProgressMetrics(loaded: number, total: number): void {
    const now = Date.now();
    const timeDiff = now - this.lastProgressTime;
    const bytesDiff = loaded - this.lastLoadedBytes;

    // Calculate progress percentage
    this.uploadProgress = Math.round(100 * loaded / total);

    // Calculate upload speed (bytes per second)
    if (timeDiff > 0) {
      this.uploadSpeed = (bytesDiff / timeDiff) * 1000; // Convert to bytes/second

      // Calculate estimated time remaining
      const remainingBytes = total - loaded;
      this.estimatedTimeRemaining = Math.round(remainingBytes / this.uploadSpeed);
    }

    this.lastProgressTime = now;
    this.lastLoadedBytes = loaded;
  }

  cancelUpload(): void {
    console.log('Cancel upload called');
    
    // Abort the HTTP request
    if (this.abortController) {
      this.abortController.abort();
    }

    // Use a small timeout to allow any pending error handler to run first
    setTimeout(() => {
      if (this.isBusy) {
        console.log('Error handler did not run, handling cancellation cleanup');

        // Unsubscribe from the upload subscription
        if (this.uploadSubscription) {
          this.uploadSubscription.unsubscribe();
          this.uploadSubscription = null;
        }

        this.handleCancellationCleanup();
      }
    }, 100);
  }

  private handleCancellationCleanup(): void {
    this.isBusy = false;
    this.removeDisabled = false;
    this.uploadDisabled = false;
    this.uploadProgress = 0;
    this.abortController = undefined;
    
    this.errorMessage = "Upload cancelled.";
    this.dialogRef.close(this.errorMessage);
  }

  formatTime(seconds: number): string {
    if (seconds < 60) {
      return `${seconds}s`; 
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}m ${secs}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getTruncatedFileName(fileName: string, maxBaseLength: number = 20): string {
    if (this.breakPointState?.breakpoints[Breakpoints.XSmall]) {
      maxBaseLength = 20;
    }
    else if (this.breakPointState?.breakpoints[Breakpoints.Small]) {
      maxBaseLength = 40;
    }
    else {
      maxBaseLength = 60;
    }

    const lastDot = fileName.lastIndexOf('.');
    if (lastDot === -1) return fileName;
    const base = fileName.substring(0, lastDot);
    const ext = fileName.substring(lastDot);
    if (base.length > maxBaseLength) {
      return base.substring(0, maxBaseLength) + '[...]' + ext;
    }
    return fileName;
  }

  closeDialog(): void {
  // Cancel any ongoing upload
  if (this.isBusy && this.abortController) {
    this.abortController.abort();
  }
  
  // Close dialog without result
  this.dialogRef.close();
}

}
